import { createSelector, createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { api, loadCoreData } from "../../common/setupApi";
import { refreshUserTokens } from "../auth/authSlice";
import { addAlert, handleApiError } from "../alerts/alertsSlice";
import { selectAlertsTexts } from "../lang/langSlice";

// lancée via <Private /> si user est authentifié, dans 2 cas de figure :
// - après login
// - à l'ouverture de l'app
// à l'ouverture de l'app si ses tokens sont dans le ls mais qu'ils ont expiré,
// on logout et affiche le message : Votre session a expiré
export const getUser = () => async (dispatch, getState) => {
  const state = getState();
  const { status } = selectUser(state);

  if (status === "fulfilled" || status === "pending") {
    return;
  }

  dispatch(userActions.getUserPending());

  dispatch(refreshUserTokens()).then((authResult) => {
    if (authResult.meta.requestStatus === "fulfilled") {
      return api
        .get("user/")
        .then((userResult) => {
          const { group, staff } = jwtDecode(authResult.payload.access);
          dispatch(
            userActions.getUserFulfilled({ ...userResult.data, group, staff })
          );
          dispatch(loadCoreData({ group, pk: userResult.data.pk }));
        })
        .catch((error) => {
          dispatch(userActions.getUserRejected(error?.response?.data));
          dispatch({ type: "auth/logout" });
          dispatch(handleApiError(error));
        });
    }

    if (authResult.error) {
      dispatch(userActions.getUserRejected("expired session"));
      dispatch({ type: "auth/logout" });
      const expiredSessionMessage =
        selectAlertsTexts(state).requestsText.auth[0];
      dispatch(addAlert(expiredSessionMessage, "info"));
    }
  });
};

const initialState = {
  status: "idle",
  data: {},
  error: null,
  isRefreshing: false, // pour le PDR
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    getUserPending: (state) => {
      state.status = "pending";
      state.error = null;
    },
    getUserFulfilled: (state, action) => {
      state.status = "fulfilled";
      state.data = action.payload;
    },
    getUserRejected: (state, action) => {
      state.status = "rejected";
      state.error = action.payload;
    },
    set: (state, action) => {
      state.data[action.payload.name] = action.payload.value;
    },
    setIsRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
    setGroup: (state, action) => {
      // pour debug
      state.data.group = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("auth/logout", () => initialState);
  },
});

export const userActions = userSlice.actions;

const _selectUser = (state) => state.user;

export const selectUser = createSelector(
  [(state) => _selectUser(state)],
  (user) => {
    const data = user.data;
    const status = user.status;
    const error = user.error;
    const isRefreshing = user.isRefreshing;
    const isLoading = status === "pending";
    return { data, status, error, isLoading, isRefreshing };
  }
);

export const selectUserPk = (state) => selectUser(state).data.pk;

const _selectUserGroup = (state) => state.user.data.group;

export const selectUserGroup = createSelector(
  [(state) => _selectUserGroup(state)],
  (group) => {
    const isPlayer = group === "Joueurs";
    const isCoach = group === "Coach";
    return { group, isPlayer, isCoach };
  }
);

export const selectUserFirebaseMessagingTokens = (state) =>
  selectUser(state).data.registration_id || [];

export const selectClubTimeZone = createSelector(
  [(state) => selectUser(state).data],
  (data) => {
    const cftz = data.cf_timezone;
    const tz = data.timezone;

    const detectedTimeZone = cftz || null;
    const clubTimeZone = tz || null;

    const isUserInAnotherTimeZone =
      clubTimeZone && detectedTimeZone && clubTimeZone !== detectedTimeZone;

    const defaultSessionTimeZone =
      clubTimeZone || detectedTimeZone || "Europe/Paris";

    return {
      clubTimeZone,
      detectedTimeZone,
      isUserInAnotherTimeZone,
      defaultSessionTimeZone,
    };
  }
);

export default userSlice.reducer;
